import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useTheme } from '@mui/styles';
import Tokens from '@uipath/apollo-core';
import React from 'react';
import { useIntl } from 'react-intl';

import type { ByoLlmCompatibilityProbeSummary } from '../../../../services/llm-gateway/LlmGateway.types';
import {
    getLlmIncompatibilityReasonTranslationKey,
    getLlmUnavailabilityReasonTranslationKey,
} from '../../../../services/llm-gateway/LlmGateway.utils';

interface ProductIncompatibilitiesDialogProps {
    modelsWithReportedIssues: Record<string, ByoLlmCompatibilityProbeSummary>;
}

export const ConnectedModelsIssuesDialogBody = ({ modelsWithReportedIssues }: ProductIncompatibilitiesDialogProps) => {
    const theme = useTheme();
    const { formatMessage: translate } = useIntl();

    return (
        <UiStack
            direction='column'>
            <UiText>
                {translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_INVALID_CONFIGURATION_CONNECTED_MODELS_ISSUES_BODY' })}
            </UiText>
            <UiStack
                direction='column'
                mt={SpacingToken.L}
                gap={SpacingToken.XS}>
                {Object.entries(modelsWithReportedIssues).map(([ model, probeSummary ]) => (
                    <UiStack
                        key={model}
                        direction='column'
                        style={{
                            borderLeft: `${Tokens.Border.BorderThickL} solid ${theme.palette.semantic.colorErrorIcon}`,
                            paddingLeft: Tokens.Padding.PadL,
                        }}
                        gap={SpacingToken.XS}>
                        <UiText>
                            <b>
                                {model + ': '}
                            </b>
                            {!probeSummary.isAvailable
                                ? translate({
                                    id: probeSummary?.unavailabilityReason
                                        ? getLlmUnavailabilityReasonTranslationKey(probeSummary.unavailabilityReason)
                                        : 'CLIENT_BYO_LLM_UNAVAILABILITY_RESPONSE_TEXT',
                                })
                                : translate({ id: getLlmIncompatibilityReasonTranslationKey(probeSummary?.incompatibilityReason ?? '') })}
                        </UiText>
                        {probeSummary.vendorRawResponse && (
                            <Accordion
                                elevation={0}
                                aria-label={translate({ id: 'CLIENT_BYO_LLM_FULL_ERROR_ACCORDION_LABEL' })}
                                style={{ backgroundColor: theme.palette.semantic.colorBackgroundGray }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <UiText>
                                        <b>
                                            {translate({ id: 'CLIENT_BYO_LLM_FULL_ERROR_ACCORDION_LABEL' })}
                                        </b>
                                    </UiText>
                                </AccordionSummary>
                                <AccordionDetails style={{
                                    borderRadius: Tokens.Border.BorderRadiusM,
                                    margin: Tokens.Padding.PadL,
                                    backgroundColor: theme.palette.semantic.colorBackground,
                                    overflow: 'auto',
                                }}>
                                    <code>
                                        {probeSummary.vendorRawResponse}
                                    </code>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </UiStack>
                ))}
            </UiStack>
        </UiStack>
    );
};
